import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { navigate, useBasePath } from "raviger";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import cs from "classnames";

// Actions
import { changeNavigation } from "../../store/actions/navigation";
import { updateAnswer } from "../../store/actions/answers";

// Utility
// import logAnalytics from "../../utils/analytics";

// Components
// import Header from "../../components/Header";
import ProgressBone from "../../components/ProgressBone";
import MobilePage from "../../components/MobilePage";
import Floor from "../../components/Floor/Floor";
// import { useScrollY } from "../../utils/useScrollY";

// Styles
import "swiper/swiper.scss";
import "swiper/components/navigation/navigation.scss";

SwiperCore.use([Navigation]);

// App HOC
const SingleQuestion = (props) => {
	const basepath = useBasePath();
	const dispatch = useDispatch();

	const currentQuestionId /*: string */ = useSelector(
		(state) => state.navigation.currentQuestion,
	);
	const currentSurveyStep /*: number */ = useSelector(
		(state) => state.navigation.surveyStep,
	);

	const questions = useSelector((state) => state.questions.questions);
	const answers = useSelector((state) => state.answers);

	const swiper = useRef(null);

	const currentQuestion /* object */ = useMemo(() => {
		return questions.find(
			(question) => String(question.id) === String(currentQuestionId),
		);
	}, [questions, currentQuestionId]);

	const floorMemo = useMemo(() => {
		return <Floor id={currentSurveyStep} />;
	}, [currentSurveyStep]);

	const progressBoneMemo = useMemo(() => {
		return <ProgressBone step={currentSurveyStep} />;
	}, [currentSurveyStep]);

	useEffect(() => {
		// slide to correct question
		swiper.current.swiper.slideTo(questions.indexOf(currentQuestion));
	}, [swiper, currentQuestion, questions]);

	useEffect(() => {
		// update the url when questions change
		if (!currentQuestion) {
			navigate(basepath + "/finish");
		} else {
			// if question isn't nested, change the url to its Question Id
			// if nested, change url to its parent id
			let urlQuestionIndex = currentQuestionId;

			if (currentQuestion.parent_id > 0)
				urlQuestionIndex = currentQuestion.parent_id;
			navigate(basepath + "/questions/" + urlQuestionIndex);
		}
	}, [currentQuestionId, questions, currentQuestion, basepath]);

	const handleSlidePrev = useCallback(() => {
		const currentIdx = swiper.current.swiper.activeIndex;
		if (currentIdx === 0) return;
		let prevIdx = currentIdx - 1;
		const prevQuestion = questions[prevIdx];

		// if prev question is nested
		if (prevQuestion.parent_id > 0) {
			const parentQuestionAnswerId = answers[prevQuestion.parent_id].answer
				.map((answer) => answer.id)
				.join();
			// check if parent didn't answer the correct answer for nested question
			if (
				String(prevQuestion.required_parent_answer_id) !==
				String(parentQuestionAnswerId)
			) {
				// skip nested question
				prevIdx -= 1;
			}
		}
		swiper.current.swiper.slideTo(prevIdx);
		dispatch(
			changeNavigation({
				currentQuestion: String(prevQuestion.id),
				surveyStep: prevQuestion.survey_step,
			}),
		);
	}, [answers, questions, dispatch]);

	const handleSlideNext = useCallback(() => {
		const currentIdx = swiper.current.swiper.activeIndex;
		if (currentIdx === questions.length - 1)
			return navigate(basepath + "/finish");
		let nextIdx = currentIdx + 1;
		let nextQuestion = questions[nextIdx];
		// if next question is nested
		if (nextQuestion.parent_id > 0) {
			const parentQuestionAnswerId = answers[nextQuestion.parent_id].answer
				.map((answer) => answer.id)
				.join();
			// check if parent didn't answer the correct answer for nested question
			if (
				String(nextQuestion.required_parent_answer_id) !==
				String(parentQuestionAnswerId)
			) {
				// skip nested question
				nextIdx += 1;
				nextQuestion = questions[nextIdx];
				// record answer as empty string
				dispatch(updateAnswer({ question: nextQuestion, answer: [] }));
			}
		}
		swiper.current.swiper.slideTo(nextIdx);
		dispatch(
			changeNavigation({
				currentQuestion: String(nextQuestion.id),
				surveyStep: nextQuestion.survey_step,
			}),
		);
	}, [answers, questions, basepath, dispatch]);

	return (
		<div className={"questionaire question-" + currentQuestionId}>
			{/* <Header /> */}
			{progressBoneMemo}
			<Swiper
				spaceBetween={0}
				slidesPerView={1}
				ref={swiper}
				allowTouchMove={false}
			>
				<div
					onClick={handleSlidePrev}
					className={cs("slide-prev swiper-button-prev", {
						hidden: swiper.current?.swiper?.activeIndex === 0,
					})}
				/>
				<div
					onClick={handleSlideNext}
					className={cs(["slide-next", "swiper-button-next"], {})}
				/>
				{questions.map((question) => (
					<SwiperSlide key={question.id}>
						<MobilePage questionData={question} />
					</SwiperSlide>
				))}
			</Swiper>
			{/* <Floor id={currentSurveyStep} /> */}
			{floorMemo}
		</div>
	);
};

export default SingleQuestion;
